import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { db } from '../../services/firebase';
import { ref, onValue, off, query, limitToLast } from 'firebase/database';
import { database, subscribeToPath } from '../../services/database';
import { toast } from 'react-hot-toast';
import { safeFormat } from '../../utils/safeDateFns';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import { Memo, MemoCategory, MemoTopic, MemoTag } from '../../types';
import Modal from '../ui/Modal';
import { motion, AnimatePresence } from 'motion/react';

interface MemoManagementViewProps {
    isAdmin?: boolean;
    staffId?: string;
    staffName?: string;
}

const MemoManagementView: React.FC<MemoManagementViewProps> = ({ isAdmin = false, staffId = '', staffName = 'Admin' }) => {
    // Data State
    const [memos, setMemos] = useState<Memo[]>([]);
    const [categories, setCategories] = useState<MemoCategory[]>([]);
    const [topics, setTopics] = useState<MemoTopic[]>([]);
    const [tags, setTags] = useState<MemoTag[]>([]);
    
    // UI State
    const [loading, setLoading] = useState(true);
    const [isAddingInModal, setIsAddingInModal] = useState(false);
    const [editingMemo, setEditingMemo] = useState<Memo | null>(null);
    const [managementTab, setManagementTab] = useState<'memos' | 'categories' | 'topics' | 'tags'>('memos');
    const [isHtmlMode, setIsHtmlMode] = useState(false);
    
    // Filter/Search State in Management
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    
    // Form State
    const [memoForm, setMemoForm] = useState<Omit<Memo, 'id' | 'createdAt' | 'updatedAt'>>({
        title: '',
        content: '',
        categoryId: '',
        topicId: '',
        tags: {},
        authorId: staffId,
        authorName: staffName,
        searchIndex: '',
        status: 'active'
    });

    // Subscriptions
    useEffect(() => {
        const unsubCategories = subscribeToPath('categories', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoCategory[] : [];
            setCategories(list);
        });
        const unsubTopics = subscribeToPath('topics', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoTopic[] : [];
            setTopics(list);
        });
        const unsubTags = subscribeToPath('tags', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoTag[] : [];
            setTags(list);
        });

        // Listen for all memos
        const memosRef = ref(db, 'memos');
        const q = query(memosRef, limitToLast(200));
        const unsubMemos = onValue(q, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const list = Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as Memo[];
                const sorted = list.filter(m => m.status !== 'deleted').sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
                });
                setMemos(sorted);
            } else {
                setMemos([]);
            }
            setLoading(false);
        });

        return () => {
            unsubCategories();
            unsubTopics();
            unsubTags();
            off(memosRef);
        };
    }, []);

    // Management Tab Handlers
    const [newCategory, setNewCategory] = useState({ name: '', color: '#4f46e5' });
    const [newTopic, setNewTopic] = useState({ name: '', categoryId: '' });
    const [newTag, setNewTag] = useState({ name: '' });

    const handleSaveMemo = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Remove HTML tags to check if there is actual text content
        const plainText = memoForm.content.replace(/<[^>]*>/g, '').trim();
        const hasImagesOrTables = memoForm.content.includes('<img') || memoForm.content.includes('<table');

        if (!memoForm.title || (!plainText && !hasImagesOrTables)) {
            toast.error("Title and meaningful content are required");
            return;
        }

        // Improved search index: convert HTML to text with spaces between tags to avoid word smashing
        const cleanText = memoForm.content
            .replace(/<\/?[^>]+(>|$)/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        
        const searchIndex = `${memoForm.title} ${cleanText}`.toLowerCase();
        
        // Critical: Ensure we are saving the raw HTML but sanitized for safety.
        // We are more permissive here to allow the "Full HTML Support" requested.
        const sanitizedContent = DOMPurify.sanitize(memoForm.content, {
            ADD_TAGS: [
                'table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot', 'colgroup', 'col', 
                'section', 'article', 'aside', 'details', 'summary', 'figure', 'figcaption',
                'span', 'div', 'br', 'hr', 'p', 'b', 'strong', 'i', 'em', 'u', 's', 'strike',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
                'img', 'video', 'a', 'iframe', 'canvas', 'svg', 'path', 'g', 'circle', 'rect',
                'style', 'center', 'font'
            ],
            ADD_ATTR: [
                'style', 'colspan', 'rowspan', 'class', 'target', 'rel', 'href', 'src', 
                'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title',
                'alt', 'loading', 'data-*', 'border', 'cellpadding', 'cellspacing', 'id',
                'align', 'valign', 'bgcolor', 'bordercolor', 'face', 'size', 'color'
            ],
            // Allow all CSS properties in the style attribute
            FORBID_ATTR: [],
            FORBID_TAGS: [],
            FORCE_BODY: true,
            ALLOW_UNKNOWN_PROTOCOLS: true
        });

        const finalForm = { ...memoForm, content: sanitizedContent, searchIndex };

        try {
            if (editingMemo) {
                await database.updateMemo(editingMemo.id, finalForm, editingMemo);
                toast.success("Memo updated");
            } else {
                await database.createMemo(finalForm);
                toast.success("Memo created");
            }
            setIsAddingInModal(false);
            setEditingMemo(null);
            resetMemoForm();
        } catch (error) {
            console.error("Error saving memo:", error);
            toast.error("Failed to save memo");
        }
    };

    const resetMemoForm = () => {
        setMemoForm({
            title: '',
            content: '',
            categoryId: '',
            topicId: '',
            tags: {},
            authorId: staffId,
            authorName: staffName,
            searchIndex: '',
            status: 'active'
        });
    };

    const handleEditMemo = (memo: Memo) => {
        setEditingMemo(memo);
        setMemoForm({
            title: memo.title,
            content: memo.content,
            categoryId: memo.categoryId,
            topicId: memo.topicId || '',
            tags: memo.tags || {},
            authorId: memo.authorId,
            authorName: memo.authorName,
            searchIndex: memo.searchIndex,
            status: memo.status || 'active'
        });
        setIsAddingInModal(true);
    };

    const handleDeleteMemo = async (memo: Memo) => {
        if (!window.confirm("Are you sure you want to delete this memo?")) return;
        try {
            await database.deleteMemo(memo.id, memo);
            toast.success("Memo deleted");
        } catch (error) {
            console.error("Error deleting memo:", error);
            toast.error("Failed to delete memo");
        }
    };

    const toggleTag = (tagId: string) => {
        setMemoForm(prev => {
            const newTags = { ...prev.tags };
            if (newTags[tagId]) {
                delete newTags[tagId];
            } else {
                newTags[tagId] = true;
            }
            return { ...prev, tags: newTags };
        });
    };

    // Quill Configuration
    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['link', 'image', 'video'],
                ['blockquote', 'code-block'],
                [{ 'color': [] }, { 'background': [] }],
                ['clean'],
                // Table support is handled by the module but button can be added if supported by theme
            ],
        },
        clipboard: {
            matchVisual: false, // Prevents extra whitespace on paste
        }
    }), []);

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list',
        'indent',
        'align',
        'link', 'image', 'video',
        'blockquote', 'code-block',
        'color', 'background'
    ];

    // Meta Handlers
    const handleCreateCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCategory.name) return;
        await database.createMemoCategory(newCategory.name, newCategory.color);
        setNewCategory({ name: '', color: '#4f46e5' });
        toast.success("Category created");
    };

    const handleCreateTopic = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTopic.name || !newTopic.categoryId) return;
        await database.createMemoTopic(newTopic.name, newTopic.categoryId);
        setNewTopic({ name: '', categoryId: '' });
        toast.success("Topic created");
    };

    const handleCreateTag = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTag.name) return;
        await database.createMemoTag(newTag.name);
        setNewTag({ name: '' });
        toast.success("Tag created");
    };

    const filteredMemos = useMemo(() => {
        return memos.filter(m => {
            const matchesSearch = searchQuery ? m.title.toLowerCase().includes(searchQuery.toLowerCase()) || m.authorName.toLowerCase().includes(searchQuery.toLowerCase()) : true;
            const matchesCategory = selectedCategory ? m.categoryId === selectedCategory : true;
            return matchesSearch && matchesCategory;
        });
    }, [memos, searchQuery, selectedCategory]);

    if (!isAdmin) {
        return (
            <div className="p-8 text-center bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700">
                <Icons.AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Permission Denied</h2>
                <p className="text-gray-500">You do not have administrative privileges to access this area.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Memo Management</h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Configure and manage staff announcements</p>
                </div>
                <button
                    onClick={() => {
                        resetMemoForm();
                        setEditingMemo(null);
                        setIsAddingInModal(true);
                    }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md shadow-indigo-500/20"
                >
                    <Icons.Plus className="w-5 h-5" />
                    Create New Memo
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
                <div className="flex overflow-x-auto border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/50">
                    {(['memos', 'categories', 'topics', 'tags'] as const).map(tab => (
                        <button
                            key={tab}
                            onClick={() => setManagementTab(tab)}
                            className={`px-6 py-4 text-sm font-bold capitalize transition-all border-b-2 whitespace-nowrap ${
                                managementTab === tab 
                                ? 'border-indigo-600 text-indigo-600 bg-white dark:bg-slate-800' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            {tab === 'memos' ? 'All Memos' : tab}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {managementTab === 'memos' && (
                        <div className="space-y-6">
                            {/* Search & Filter */}
                            <div className="flex flex-wrap gap-4">
                                <div className="flex-1 min-w-[250px] relative">
                                    <Icons.Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-11 pr-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Search by title or author..."
                                    />
                                </div>
                                <select
                                    value={selectedCategory}
                                    onChange={e => setSelectedCategory(e.target.value)}
                                    className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-slate-700">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-slate-900/50">
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Title</th>
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Created By</th>
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                                    <Icons.Loader className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                                                    Loading memos...
                                                </td>
                                            </tr>
                                        ) : filteredMemos.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500 font-medium">
                                                    No memos found matches your criteria.
                                                </td>
                                            </tr>
                                        ) : filteredMemos.map(memo => {
                                            const category = categories.find(c => c.id === memo.categoryId);
                                            return (
                                                <tr key={memo.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                                    <td className="px-4 py-4">
                                                        <span className="font-bold text-gray-900 dark:text-white">{memo.title}</span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {category ? (
                                                            <span 
                                                                className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider"
                                                                style={{ backgroundColor: `${category.color}20`, color: category.color }}
                                                            >
                                                                {category.name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">Uncategorized</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 uppercase">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                            memo.status === 'active' 
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                                                        }`}>
                                                            {memo.status || 'Active'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{memo.authorName}</span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            {safeFormat(memo.createdAt, 'MMM d, yyyy')}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button 
                                                                onClick={() => handleEditMemo(memo)}
                                                                className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all"
                                                                title="Edit Memo"
                                                            >
                                                                <Icons.Edit className="w-4 h-4" />
                                                            </button>
                                                            <button 
                                                                onClick={() => handleDeleteMemo(memo)}
                                                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                                                title="Delete Memo"
                                                            >
                                                                <Icons.Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {managementTab === 'categories' && (
                        <div className="space-y-6 max-w-4xl">
                            <form onSubmit={handleCreateCategory} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end bg-gray-50 dark:bg-slate-900/50 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Category Name</label>
                                    <input
                                        type="text"
                                        value={newCategory.name}
                                        onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="e.g. Policies"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Color</label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={newCategory.color}
                                            onChange={e => setNewCategory({ ...newCategory, color: e.target.value })}
                                            className="h-10 w-20 p-1 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer"
                                        />
                                        <div className="flex-1 text-[10px] font-mono text-gray-500 uppercase">{newCategory.color}</div>
                                    </div>
                                </div>
                                <button type="submit" className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all">Add Category</button>
                            </form>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {categories.map(category => (
                                    <div 
                                        key={category.id}
                                        className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                                            <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{category.name}</span>
                                        </div>
                                        <button 
                                            onClick={() => database.deleteMemoCategory(category.id)}
                                            className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Icons.X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {managementTab === 'topics' && (
                        <div className="space-y-6 max-w-5xl">
                            <form onSubmit={handleCreateTopic} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end bg-gray-50 dark:bg-slate-900/50 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Topic Name</label>
                                    <input
                                        type="text"
                                        value={newTopic.name}
                                        onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="e.g. Attendance"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Parent Category</label>
                                    <select
                                        value={newTopic.categoryId}
                                        onChange={e => setNewTopic({ ...newTopic, categoryId: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <button type="submit" className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all">Add Topic</button>
                            </form>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {topics.map(topic => {
                                    const category = categories.find(c => c.id === topic.categoryId);
                                    return (
                                        <div 
                                            key={topic.id}
                                            className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm group"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{topic.name}</span>
                                                {category && <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{category.name}</span>}
                                            </div>
                                            <button 
                                                onClick={() => database.deleteMemoTopic(topic.id)}
                                                className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                            >
                                                <Icons.X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {managementTab === 'tags' && (
                        <div className="space-y-6 max-w-4xl">
                            <form onSubmit={handleCreateTag} className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-slate-900/50 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Tag Name</label>
                                    <input
                                        type="text"
                                        value={newTag.name}
                                        onChange={e => setNewTag({ ...newTag, name: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="e.g. System Update"
                                    />
                                </div>
                                <button type="submit" className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all">Add Tag</button>
                            </form>

                            <div className="flex flex-wrap gap-3">
                                {tags.map(tag => (
                                    <div 
                                        key={tag.id}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm group"
                                    >
                                        <span className="text-xs font-black text-indigo-600">#</span>
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200 pr-2">{tag.name}</span>
                                        <button 
                                            onClick={() => database.deleteMemoTag(tag.id)}
                                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all border-l border-gray-100 dark:border-slate-700 ml-1"
                                        >
                                            <Icons.X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isAddingInModal && (
                <Modal
                    onClose={() => setIsAddingInModal(false)}
                    title={editingMemo ? "Edit Memo" : "Create New Memo"}
                    maxWidthClass="sm:max-w-4xl"
                >
                    <style>{`
                        .rich-text-content table {
                            border-collapse: collapse;
                            width: 100%;
                            margin-bottom: 1em;
                        }
                        .rich-text-content table td, .rich-text-content table th {
                            border: 1px solid #ddd;
                            padding: 8px;
                        }
                        .dark .rich-text-content table td, .dark .rich-text-content table th {
                            border-color: #334155;
                        }
                        .memo-editor-container .ql-editor {
                            min-height: 350px;
                        }
                        .memo-editor-container .ql-toolbar {
                            border-top-left-radius: 0.75rem;
                            border-top-right-radius: 0.75rem;
                            border-color: #e2e8f0;
                            background-color: #f8fafc;
                        }
                        .dark .memo-editor-container .ql-toolbar {
                            border-color: #334155;
                            background-color: #0f172a;
                        }
                        .memo-editor-container .ql-container {
                            border-bottom-left-radius: 0.75rem;
                            border-bottom-right-radius: 0.75rem;
                            border-color: #e2e8f0;
                        }
                        .dark .memo-editor-container .ql-container {
                            border-color: #334155;
                        }
                    `}</style>
                    <form onSubmit={handleSaveMemo} className="space-y-6 overflow-y-auto px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Memo Title</label>
                            <input
                                type="text"
                                value={memoForm.title}
                                onChange={e => setMemoForm({ ...memoForm, title: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 text-lg font-bold"
                                placeholder="Enter title..."
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Category</label>
                            <select
                                value={memoForm.categoryId}
                                onChange={e => setMemoForm({ ...memoForm, categoryId: e.target.value, topicId: '' })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Topic</label>
                            <select
                                value={memoForm.topicId}
                                onChange={e => setMemoForm({ ...memoForm, topicId: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                disabled={!memoForm.categoryId}
                            >
                                <option value="">None / Top Level</option>
                                {topics.filter(t => t.categoryId === memoForm.categoryId).map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                         <div className="md:col-span-3">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Tags</label>
                            <div className="flex flex-wrap gap-2">
                                {tags.map(tag => (
                                    <button
                                        key={tag.id}
                                        type="button"
                                        onClick={() => toggleTag(tag.id)}
                                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                                            memoForm.tags[tag.id]
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                                        }`}
                                    >
                                        {tag.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Visibility Status</label>
                            <select
                                value={memoForm.status}
                                onChange={e => setMemoForm({ ...memoForm, status: e.target.value as any })}
                                className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-indigo-500 font-bold ${
                                    memoForm.status === 'active' 
                                    ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/10 dark:border-green-800' 
                                    : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-slate-900 dark:border-slate-700'
                                }`}
                            >
                                <option value="active">Active (Visible)</option>
                                <option value="inactive">Inactive (Hidden)</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-bold text-gray-500 uppercase">Memo Content</label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsHtmlMode(!isHtmlMode)}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-bold uppercase transition-all ${
                                        isHtmlMode 
                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' 
                                        : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400'
                                    }`}
                                >
                                    {isHtmlMode ? <Icons.Layout className="w-3 h-3" /> : <Icons.Code className="w-3 h-3" />}
                                    {isHtmlMode ? 'Visual Editor' : 'Edit HTML Source'}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 min-h-[400px] memo-editor-container flex flex-col">
                            {isHtmlMode ? (
                                <textarea
                                    value={memoForm.content}
                                    onChange={e => setMemoForm({ ...memoForm, content: e.target.value })}
                                    className="flex-1 w-full p-4 font-mono text-sm bg-slate-950 text-emerald-400 outline-none resize-none min-h-[400px]"
                                    placeholder="Paste your HTML here..."
                                />
                            ) : (
                                <ReactQuill
                                    theme="snow"
                                    value={memoForm.content}
                                    onChange={content => setMemoForm({ ...memoForm, content })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    className="h-full rich-text-content flex-1 flex flex-col"
                                />
                            )}
                        </div>
                        {isHtmlMode && (
                            <p className="mt-2 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                <Icons.AlertTriangle className="w-3 h-3 inline mr-1" />
                                HTML Mode: Be careful with your tags. Malicious scripts will be stripped on save.
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-slate-700">
                        <button
                            type="button"
                            onClick={() => setIsAddingInModal(false)}
                            className="px-6 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-8 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 transition-all"
                        >
                            {editingMemo ? 'Save Changes' : 'Publish Memo'}
                        </button>
                    </div>
                </form>
            </Modal>
            )}
        </div>
    );
};

export default MemoManagementView;
